import type { TFactTrajectoryStationWriteDto, TFactTrajectoryWriteDto } from '../../types/fact-trajectory-write';
import type {
  TFactTrajectoryCreateDefaults,
  TTrajectory,
  TTrajectoryMeasure,
  TTrajectoryStation,
  TTrajectoryValue,
} from '../../types/trajectory';
import type { FactTrajectoryEntity, TFactTrajectoryData } from '../entities';
import { FACT_TRAJECTORY_POINT_KEYS, type TFactTrajectoryPoint } from '../model';

import { mapApiStationToFactPoint } from './map-api-trajectory';
import { resolveActiveFlag } from './map-fact-trajectory';

/** Local new trajectories / stations use negative or missing ids that must not be sent to the backend. */
function resolveWriteId(id: number | null | undefined): number | undefined {
  if (id == null || id <= 0) {
    return undefined;
  }

  return id;
}

function numericField(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }

  return value;
}

function measureValue(measure: { value: number } | undefined | null): number | undefined {
  return numericField(measure?.value);
}

function readTrajectoryValue(value: number | TTrajectoryValue | undefined | null): number | undefined {
  if (typeof value === 'number') {
    return numericField(value);
  }

  return measureValue(value);
}

function toTrajectoryValue(value: number | TTrajectoryValue | undefined | null): TTrajectoryValue | undefined {
  const numericValue = readTrajectoryValue(value);

  if (numericValue === undefined) {
    return undefined;
  }

  return { value: numericValue };
}

function measureUom(measure: { uom?: string } | undefined | null): string | undefined {
  return measure?.uom || undefined;
}

function readBaseReferencePointUuid(
  datum: { baseReferencePoint?: { uuid?: unknown } | Record<string, unknown> } | null | undefined,
): string | undefined {
  const base = datum?.baseReferencePoint;

  if (!base || typeof base !== 'object' || !('uuid' in base)) {
    return undefined;
  }

  return typeof base.uuid === 'string' && base.uuid.length > 0 ? base.uuid : undefined;
}

function toDatumWriteDto(
  datum: { baseReferencePoint?: { uuid?: unknown } | Record<string, unknown> } | null | undefined,
): TFactTrajectoryWriteDto['defaultMdDatum'] | undefined {
  const uuid = readBaseReferencePointUuid(datum);

  if (!uuid) {
    return undefined;
  }

  return {
    baseReferencePoint: {
      uuid,
      qualifiedType: 'ReferencePointInACrs',
    },
  };
}

function mapPointToStationDto(point: { data: TFactTrajectoryPoint }): TFactTrajectoryStationWriteDto {
  const dto: TFactTrajectoryStationWriteDto = {
    mdMeasuredDepth: numericField(point.data.md),
    incl: numericField(point.data.inc),
    azi: numericField(point.data.azi),
    mtf: numericField(point.data.mtf),
    gtf: numericField(point.data.gtf),
  };

  const stationId = resolveWriteId(point.data.stationId);

  if (stationId !== undefined) {
    dto.id = stationId;
  }

  if (point.data.typeTrajStation != null) {
    dto.typeTrajStation = point.data.typeTrajStation;
  }

  if (point.data.comment != null) {
    dto.appComment = point.data.comment;
  }

  return dto;
}

function hadInitialScalar<T>(initial: T | undefined | null): boolean {
  return initial !== undefined && initial !== null;
}

function writeNullableNumberField(
  dto: TFactTrajectoryWriteDto,
  key:
    | 'organizationId'
    | 'parentTrajectoryId'
    | 'mdTieOn'
    | 'gridConUsed'
    | 'magDeclUsed'
    | 'aziVertSect'
    | 'depthUomId'
    | 'severityUomId'
    | 'coordinatesUomId',
  current: number | undefined,
  initial: number | undefined | null,
  isExisting: boolean,
): void {
  if (typeof current === 'number' && !Number.isNaN(current)) {
    dto[key] = current;
    return;
  }

  if (isExisting && hadInitialScalar(initial)) {
    dto[key] = null;
  }
}

function writeNullableStringField(
  dto: TFactTrajectoryWriteDto,
  key: 'title' | 'description' | 'aziRef' | 'mdTieOnUom',
  current: string | undefined,
  initial: string | undefined | null,
  isExisting: boolean,
): void {
  if (current !== undefined && current !== null && current !== '') {
    dto[key] = current;
    return;
  }

  if (isExisting && hadInitialScalar(initial) && initial !== '') {
    dto[key] = null;
  }
}

function writeNullableDatumField(
  dto: TFactTrajectoryWriteDto,
  key: 'defaultMdDatum' | 'defaultTvdDatum',
  current: ReturnType<typeof toDatumWriteDto>,
  initialUuid: string | undefined,
  isExisting: boolean,
): void {
  if (current) {
    dto[key] = current;
    return;
  }

  if (isExisting && hadInitialScalar(initialUuid)) {
    dto[key] = null;
  }
}

export function mapEntityToFactWriteDto(entity: FactTrajectoryEntity): TFactTrajectoryWriteDto {
  const data = entity.data;
  const initial = entity.initialData;
  const isExisting = data.id > 0;
  const isActive = Boolean(data.active ?? data.definitive);

  const dto: TFactTrajectoryWriteDto = {
    wellboreId: data.wellboreId,
    trajectoryStations: entity.points.map(mapPointToStationDto),
  };

  const id = resolveWriteId(data.id);

  if (id !== undefined) {
    dto.id = id;
  }

  if (data.dtoIndex !== undefined) {
    dto.dtoIndex = data.dtoIndex;
  }

  if (data.objectVersion) {
    dto.objectVersion = data.objectVersion;
  }

  writeNullableStringField(dto, 'title', data.citation.title, initial.citation?.title, isExisting);
  writeNullableStringField(dto, 'description', data.citation.description, initial.citation?.description, isExisting);

  writeNullableNumberField(dto, 'organizationId', data.organizationId, initial.organizationId, isExisting);

  dto.activeStatus = isActive ? 'ACTIVE' : 'INACTIVE';

  if (data.finalTraj !== undefined) {
    dto.finalTraj = data.finalTraj;
  }

  writeNullableNumberField(dto, 'parentTrajectoryId', data.parentTrajectoryId, initial.parentTrajectoryId, isExisting);

  writeNullableNumberField(
    dto,
    'mdTieOn',
    measureValue(data.mdTieOn?.measuredDepth),
    measureValue(initial.mdTieOn?.measuredDepth),
    isExisting,
  );

  const mdTieOnUom = measureUom(data.mdTieOn?.measuredDepth);
  writeNullableStringField(dto, 'mdTieOnUom', mdTieOnUom, measureUom(initial.mdTieOn?.measuredDepth), isExisting);

  writeNullableStringField(dto, 'aziRef', data.aziRef, initial.aziRef, isExisting);

  writeNullableNumberField(
    dto,
    'gridConUsed',
    measureValue(data.gridConUsed),
    measureValue(initial.gridConUsed),
    isExisting,
  );
  writeNullableNumberField(
    dto,
    'magDeclUsed',
    measureValue(data.magDeclUsed),
    measureValue(initial.magDeclUsed),
    isExisting,
  );
  writeNullableNumberField(
    dto,
    'aziVertSect',
    measureValue(data.aziVertSect),
    measureValue(initial.aziVertSect),
    isExisting,
  );

  writeNullableNumberField(dto, 'depthUomId', data.depthUomId, initial.depthUomId, isExisting);
  writeNullableNumberField(dto, 'severityUomId', data.severityUomId, initial.severityUomId, isExisting);
  writeNullableNumberField(dto, 'coordinatesUomId', data.coordinatesUomId, initial.coordinatesUomId, isExisting);

  writeNullableDatumField(
    dto,
    'defaultMdDatum',
    toDatumWriteDto(data.defaultMdDatum),
    readBaseReferencePointUuid(initial.defaultMdDatum),
    isExisting,
  );
  writeNullableDatumField(
    dto,
    'defaultTvdDatum',
    toDatumWriteDto(data.defaultTvdDatum),
    readBaseReferencePointUuid(initial.defaultTvdDatum),
    isExisting,
  );

  return dto;
}

const POINT_FIELDS: ReadonlySet<string> = new Set(FACT_TRAJECTORY_POINT_KEYS);

function isPointField(key: string): key is keyof TFactTrajectoryPoint {
  return POINT_FIELDS.has(key);
}

function toMeasure(value: number, previous?: TTrajectoryMeasure, uom?: string): TTrajectoryMeasure {
  return {
    value,
    uom: uom || previous?.uom,
    uomId: previous?.uomId,
  };
}

function toValueFromWriteDto(value: number | TTrajectoryValue | undefined): TTrajectoryValue | undefined {
  return toTrajectoryValue(value);
}

function buildDataPatchFromTrajectoryResponse(
  entity: FactTrajectoryEntity,
  response: TTrajectory & { activeStatus?: TFactTrajectoryWriteDto['activeStatus'] },
): Partial<Omit<TFactTrajectoryData, 'citation'>> & { citation?: Partial<TFactTrajectoryData['citation']> } {
  const patch: Partial<Omit<TFactTrajectoryData, 'citation'>> & {
    citation?: Partial<TFactTrajectoryData['citation']>;
  } = {
    id: response.id,
    objectVersion: response.objectVersion,
    wellboreId: response.wellboreId,
    finalTraj: response.finalTraj,
    parentTrajectoryId: response.parentTrajectoryId,
    actualTrajId: response.actualTrajId,
    defaultMdDatum: response.defaultMdDatum,
    defaultTvdDatum: response.defaultTvdDatum,
    aziRef: response.aziRef,
    depthUomId: response.depthUomId,
    severityUomId: response.severityUomId,
    coordinatesUomId: response.coordinatesUomId,
    organizationId: response.organizationId,
    citation: {
      title: response.citation?.title,
      description: response.citation?.description,
    },
  };

  const active = resolveActiveFlag(response);

  if (active !== undefined) {
    patch.active = active;
  }

  const gridConUsed = readTrajectoryValue(response.gridConUsed);
  if (gridConUsed !== undefined) {
    patch.gridConUsed = { value: gridConUsed };
  }

  const magDeclUsed = readTrajectoryValue(response.magDeclUsed);
  if (magDeclUsed !== undefined) {
    patch.magDeclUsed = { value: magDeclUsed };
  }

  const aziVertSect = readTrajectoryValue(response.aziVertSect);
  if (aziVertSect !== undefined) {
    patch.aziVertSect = { value: aziVertSect };
  }

  const mdTieOnValue = measureValue(response.mdTieOn?.measuredDepth);
  if (mdTieOnValue !== undefined) {
    patch.mdTieOn = {
      ...entity.data.mdTieOn,
      measuredDepth: toMeasure(mdTieOnValue, entity.data.mdTieOn?.measuredDepth, response.mdTieOn?.measuredDepth?.uom),
    };
  }

  return patch;
}

function readStationNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'object' && value && 'value' in value) {
    const nested = (value as { value?: unknown }).value;
    if (typeof nested === 'number' && !Number.isNaN(nested)) {
      return nested;
    }
  }

  return undefined;
}

function isApiTrajectoryStation(station: unknown): station is TTrajectoryStation {
  if (typeof station !== 'object' || !station) {
    return false;
  }

  if (!('md' in station) || typeof station.md !== 'object' || !station.md) {
    return false;
  }

  return 'measuredDepth' in station.md;
}

export function mapWriteStationToPointPatch(
  station: TFactTrajectoryStationWriteDto | TTrajectoryStation,
): Partial<TFactTrajectoryPoint> {
  if (isApiTrajectoryStation(station)) {
    return mapApiStationToFactPoint(station);
  }

  const patch: Partial<TFactTrajectoryPoint> = {};
  const record = station as TFactTrajectoryStationWriteDto & Partial<TFactTrajectoryPoint>;

  for (const key of Object.keys(record)) {
    switch (key) {
      case 'id':
        if (record.id !== undefined) {
          patch.stationId = record.id;
        }
        break;
      case 'mdMeasuredDepth':
        if (readStationNumber(record.mdMeasuredDepth) !== undefined) {
          patch.md = readStationNumber(record.mdMeasuredDepth);
        }
        break;
      case 'incl':
        if (readStationNumber(record.incl) !== undefined) {
          patch.inc = readStationNumber(record.incl);
        }
        break;
      case 'azi':
        if (readStationNumber(record.azi) !== undefined) {
          patch.azi = readStationNumber(record.azi);
        }
        break;
      case 'appComment':
        if (record.appComment !== undefined) {
          patch.comment = record.appComment;
        }
        break;
      default:
        if (isPointField(key) && record[key] !== undefined) {
          (patch as Record<string, unknown>)[key] = record[key];
        }
        break;
    }
  }

  return patch;
}

function buildDataPatchFromWriteDto(
  entity: FactTrajectoryEntity,
  response: TFactTrajectoryWriteDto,
): Partial<Omit<TFactTrajectoryData, 'citation'>> & { citation?: Partial<TFactTrajectoryData['citation']> } {
  const patch: Partial<Omit<TFactTrajectoryData, 'citation'>> & {
    citation?: Partial<TFactTrajectoryData['citation']>;
  } = {};
  const citation: Partial<TFactTrajectoryData['citation']> = {};

  for (const key of Object.keys(response) as Array<keyof TFactTrajectoryWriteDto>) {
    switch (key) {
      case 'id':
        if (response.id !== undefined) {
          patch.id = response.id;
        }
        break;
      case 'dtoIndex':
        if (response.dtoIndex !== undefined) {
          patch.dtoIndex = response.dtoIndex;
        }
        break;
      case 'objectVersion':
        if (response.objectVersion !== undefined) {
          patch.objectVersion = response.objectVersion;
        }
        break;
      case 'wellboreId':
        if (response.wellboreId !== undefined) {
          patch.wellboreId = response.wellboreId;
        }
        break;
      case 'title':
        if (response.title !== undefined) {
          citation.title = response.title ?? '';
        }
        break;
      case 'description':
        if (response.description !== undefined) {
          citation.description = response.description ?? undefined;
        }
        break;
      case 'organizationId':
        if (response.organizationId !== undefined) {
          patch.organizationId = response.organizationId ?? undefined;
        }
        break;
      case 'activeStatus':
        if (response.activeStatus !== undefined) {
          patch.active = response.activeStatus === 'ACTIVE';
        }
        break;
      case 'finalTraj':
        if (response.finalTraj !== undefined) {
          patch.finalTraj = response.finalTraj;
        }
        break;
      case 'parentTrajectoryId':
        if (response.parentTrajectoryId !== undefined) {
          patch.parentTrajectoryId = response.parentTrajectoryId ?? undefined;
        }
        break;
      case 'aziRef':
        if (response.aziRef !== undefined) {
          patch.aziRef = response.aziRef ?? undefined;
        }
        break;
      case 'gridConUsed':
        if (response.gridConUsed !== undefined) {
          patch.gridConUsed = response.gridConUsed === null ? undefined : toValueFromWriteDto(response.gridConUsed);
        }
        break;
      case 'magDeclUsed':
        if (response.magDeclUsed !== undefined) {
          patch.magDeclUsed = response.magDeclUsed === null ? undefined : toValueFromWriteDto(response.magDeclUsed);
        }
        break;
      case 'aziVertSect':
        if (response.aziVertSect !== undefined) {
          patch.aziVertSect = response.aziVertSect === null ? undefined : toValueFromWriteDto(response.aziVertSect);
        }
        break;
      case 'depthUomId':
        if (response.depthUomId !== undefined) {
          patch.depthUomId = response.depthUomId ?? undefined;
        }
        break;
      case 'severityUomId':
        if (response.severityUomId !== undefined) {
          patch.severityUomId = response.severityUomId ?? undefined;
        }
        break;
      case 'coordinatesUomId':
        if (response.coordinatesUomId !== undefined) {
          patch.coordinatesUomId = response.coordinatesUomId ?? undefined;
        }
        break;
      case 'defaultMdDatum':
        if (response.defaultMdDatum !== undefined) {
          patch.defaultMdDatum = response.defaultMdDatum
            ? {
                baseReferencePoint: response.defaultMdDatum.baseReferencePoint,
              }
            : undefined;
        }
        break;
      case 'defaultTvdDatum':
        if (response.defaultTvdDatum !== undefined) {
          patch.defaultTvdDatum = response.defaultTvdDatum
            ? {
                baseReferencePoint: response.defaultTvdDatum.baseReferencePoint,
              }
            : undefined;
        }
        break;
      default:
        break;
    }
  }

  if (response.mdTieOn !== undefined || response.mdTieOnUom !== undefined) {
    const previous = entity.data.mdTieOn?.measuredDepth;

    if (response.mdTieOn === null) {
      patch.mdTieOn = undefined;
    } else {
      const value = response.mdTieOn !== undefined ? response.mdTieOn : previous?.value;
      const uom = response.mdTieOnUom !== undefined ? (response.mdTieOnUom ?? undefined) : previous?.uom;

      if (typeof value === 'number') {
        patch.mdTieOn = {
          ...entity.data.mdTieOn,
          measuredDepth: toMeasure(value, previous, uom),
        };
      }
    }
  }

  if (Object.keys(citation).length > 0) {
    patch.citation = citation;
  }

  return patch;
}

export function applyFactWriteDtoToEntity(
  entity: FactTrajectoryEntity,
  response: TFactTrajectoryWriteDto | TTrajectory | null | undefined,
): void {
  if (!response) {
    entity.commitData();
    return;
  }

  const patch =
    'citation' in response
      ? buildDataPatchFromTrajectoryResponse(entity, response)
      : buildDataPatchFromWriteDto(entity, response);
  entity.updateData(patch);

  if (Array.isArray(response.trajectoryStations)) {
    entity.syncPointsFromPatches(response.trajectoryStations.map((station) => mapWriteStationToPointPatch(station)));
  }

  entity.commitData();
}

/** Prefill a local (negative id) trajectory from GET create-defaults. Does not persist or rewrite id. */
export function applyTrajectoryCreateDefaultsToEntity(
  entity: FactTrajectoryEntity,
  response: TFactTrajectoryCreateDefaults,
): void {
  const patch = buildDataPatchFromTrajectoryResponse(entity, {
    ...response,
    id: entity.id,
    citation: response.citation ?? entity.data.citation,
    trajectoryStations: response.trajectoryStations ?? [],
    wellboreId: entity.wellboreId,
  });
  delete patch.id;
  delete patch.wellboreId;

  entity.updateData(patch);

  if (Array.isArray(response.trajectoryStations) && response.trajectoryStations.length > 0) {
    entity.syncPointsFromPatches(response.trajectoryStations.map((station) => mapWriteStationToPointPatch(station)));
  }
}
